/**
 * Channel view header row (W2-04b curve-editor; DESIGN.md §4.7 "Channel view shows one property across all layers
 * plus a read-only Σ column with 'Flatten to layer 0'", §5.6.3).
 *
 * The canvas below draws one lane per layer and the Σ lane (curveColumn.ts); this row labels each lane at the same
 * x as its lane: layer name with its state (filled dot = has keys, hollow = empty, crossed eye = muted in the
 * preview; a click edits that layer), and above the Σ lane "Sum of layers" (read-only) with "Flatten to layer 0",
 * which runs W2-11's contracted `tools.flatten` through the registry.
 */

import type { AppServices } from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import { Icon, IconButton } from '../../ui';
import { flattenLayers } from './curveActions';
import type { CurveView, LaneInfo } from './curveColumn';
import { m } from './curveEditor.messages';
import s from './CurveEditor.module.css';
import { layerCount, layerLabel } from './curveModel';

export interface ChannelViewProps {
  readonly services: AppServices;
  readonly view: Pick<CurveView, 'target' | 'lanes'>;
}

/** Why "Flatten to layer 0" is unavailable, or null. */
export function flattenReason(
  services: Pick<AppServices, 'store' | 'editor'>,
  view: Pick<CurveView, 'target'>,
): MessageRef | null {
  const store = services.store;
  if (store === null) return { key: m.noProject };
  if (services.editor.getState().filters.lockedLines.has(view.target.line)) return { key: m.lineLocked };
  if (layerCount(store.doc, store.idx, view.target.line, view.target.prop) < 2) return { key: m.flattenNothing };
  return null;
}

function LaneLabel(props: {
  readonly services: AppServices;
  readonly view: ChannelViewProps['view'];
  readonly lane: LaneInfo;
}) {
  const { services, view, lane } = props;
  const store = services.store;
  const i18n = services.i18n;
  const st = services.editor.getState();
  const style = { left: `${String(lane.x0)}px`, width: `${String(lane.width)}px` };
  if (lane.layer === 'sum') {
    const reason = flattenReason(services, view);
    return (
      <div className={s.lane} style={style} title={`${i18n.t(m.sigmaLaneHint)} ${i18n.t(m.flattenHint)}`}>
        <Icon name="Sigma" size={12} />
        <span className={s.laneText}>{i18n.t(m.sigmaLane)}</span>
        <IconButton
          size="sm"
          icon="Merge"
          label={{ key: m.flattenButton }}
          command="curve.flatten"
          disabledReason={reason}
          onClick={() => {
            void flattenLayers(services, view.target);
          }}
        />
      </div>
    );
  }
  const layer = lane.layer;
  const name = store === null ? '' : layerLabel(store.doc, view.target.line, layer, i18n);
  const has = lane.track !== undefined;
  const muted = st.filters.mutedLayers.get(view.target.line)?.has(`${view.target.prop}:${String(layer)}`) === true;
  const focused =
    st.focusedColumn !== null &&
    st.focusedColumn.line === view.target.line &&
    st.focusedColumn.prop === view.target.prop &&
    st.focusedColumn.layer === layer;
  const hint = i18n.t(m.layerChipHint, { name, state: has ? 'keys' : 'empty', muted: muted ? 'yes' : 'no' });
  return (
    <div className={s.lane} style={style}>
      <button
        type="button"
        className={s.chip}
        aria-pressed={focused}
        aria-label={hint}
        title={hint}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => {
          const ed = services.editor.getState();
          const ref = { line: view.target.line, prop: view.target.prop, layer };
          ed.setFocusedColumn(ref);
          if (ed.activeLine === ref.line) ed.setActiveLayer(layer);
        }}
      >
        <Icon name={has ? 'CircleDot' : 'Circle'} size={12} />
        <span className={s.laneText}>{name}</span>
        {muted ? <Icon name="EyeOff" size={12} label={{ key: m.layerMuted }} /> : null}
      </button>
    </div>
  );
}

/** One label per lane, aligned with the lanes of the channel view canvas. */
export function ChannelView(props: ChannelViewProps) {
  const lanes = props.view.lanes();
  return (
    <div className={s.channelRow} role="group" aria-label={props.services.i18n.t(m.channelColumnTitle)}>
      {lanes.map((lane) => (
        <LaneLabel key={lane.id} services={props.services} view={props.view} lane={lane} />
      ))}
    </div>
  );
}
